import Card from '../../components/Card.jsx';
import Button from '../../components/Button.jsx';
import StatCard from '../../components/StatCard.jsx';
import Chip from '../../components/Chip.jsx';
import DataTable from '../../components/DataTable.jsx';
import EquityCurveChart from './EquityCurveChart.jsx';
import YearlyMetricsTable from './YearlyMetricsTable.jsx';
import ErrorNote from './ErrorNote.jsx';
import { useState } from 'react';
import { date, money, number, percent } from '../../utils/format.js';
import { buildBacktestMetricsCsv, downloadMetricsCsv } from '../../utils/metricsExport.js';

const TRADE_COLUMNS = [
  { key: 'entryDate', header: 'Entry date', render: (r) => date(r.entryDate || r.entry_date || r.date) },
  { key: 'exitDate', header: 'Exit date', render: (r) => date(r.exitDate || r.exit_date) },
  { key: 'symbol', header: 'Symbol' },
  {
    key: 'side',
    header: 'Side',
    render: (r) => <Chip tone={['BUY', 'long'].includes(r.side) ? 'gain' : 'loss'}>{r.side}</Chip>,
  },
  { key: 'qty', header: 'Qty', numeric: true, render: (r) => number(r.qty, Number.isInteger(Number(r.qty)) ? 0 : 4) },
  { key: 'entryPrice', header: 'Entry price', numeric: true, render: (r) => money(r.entryPrice ?? r.entry_price ?? r.price, r.currency) },
  { key: 'exitPrice', header: 'Exit price', numeric: true, render: (r) => money(r.exitPrice ?? r.exit_price, r.currency) },
  {
    key: 'pnl',
    header: 'P&L',
    numeric: true,
    render: (r) => <span className={r.pnl >= 0 ? 'aq-num-gain' : 'aq-num-loss'}>{money(r.pnl, r.currency)}</span>,
  },
];

/**
 * The shared results renderer for any mode whose run produces the §2
 * NORMALIZED shape: { equity, metrics:{overall,byYear}, trades, meta,
 * credit? }. Equity curve + the MANDATORY year-by-year table + overall
 * stat cards + a trades table, plus credit-charge readout and 402 handling.
 */
export default function ResultsPanel({ result, error, running }) {
  const [page, setPage] = useState(0);
  const active = running || ['submitted', 'validating', 'preparing_data', 'queued', 'running', 'finalizing', 'recovering'].includes(result?.status);
  const completed = result && (!result.status || result.status === 'complete');
  const metrics = result?.metrics?.overall || {};
  const trades = result?.trades || [];
  const pages = Math.max(1, Math.ceil(trades.length / 25));
  const currentPage = Math.min(page, pages - 1);
  const invalid = result?.integrity?.status === 'invalid' || result?.equity?.some((p) => Number(p.equity) < 0);
  return (
    <Card>
      <h3 className="heading" style={{ fontSize: 16, marginBottom: 12 }}>
        Results
      </h3>

      {active && <p role="status" aria-live="polite" className="aq-note">Backtest {result?.status || 'running'}… You can navigate away; check My jobs for progress.</p>}
      {active && completed && <p className="aq-note">The chart below is the previous completed run.</p>}
      {invalid && <p role="alert" className="aq-note">Invalid historical simulation: negative equity or failed accounting checks. Do not use these metrics to evaluate a strategy. This result has not been clipped or corrected.</p>}
      {result?.status && ['failed', 'error', 'cancelled'].includes(result.status) && <p role="alert">{result.error || `Run ${result.status}`}</p>}
      <EquityCurveChart equity={completed ? result?.equity : []} />

      <ErrorNote error={error} />

      {completed && (
        <>
          <div className="aq-grid aq-grid--4" style={{ marginTop: 16 }}>
            <StatCard label="CAGR" value={percent(metrics.cagr)} />
            <StatCard label="Sharpe" value={number(metrics.sharpe)} />
            <StatCard
              label="Max drawdown"
              value={percent(metrics.maxDrawdown)}
            />
            <StatCard label="Win rate" value={percent(metrics.winRate)} />
          </div>

          <h4 className="heading" style={{ fontSize: 14, margin: '16px 0 8px' }}>
            Year-by-year
          </h4>
          <YearlyMetricsTable rows={result.metrics?.byYear} />

          <div className="aq-metric-row">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => downloadMetricsCsv(
                buildBacktestMetricsCsv(result),
                'alphaqap-backtest-performance-metrics.csv'
              )}
            >
              Export performance metrics (CSV)
            </Button>
            <span className="aq-note">Derived metrics only; no historical prices or market-data series.</span>
          </div>

          <h4 className="heading" style={{ fontSize: 14, margin: '16px 0 8px' }}>
            Trades ({result.trades?.length ?? 0})
          </h4>
          <DataTable columns={TRADE_COLUMNS} rows={trades.slice(currentPage * 25, (currentPage + 1) * 25)} emptyLabel="No trades." />
          <div className="aq-metric-row">
            <button type="button" disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>Previous</button>
            <span>Page {currentPage + 1} of {pages} · {trades.length} trades</span>
            <button type="button" disabled={currentPage + 1 >= pages} onClick={() => setPage(currentPage + 1)}>Next</button>
          </div>
          {!result.meta?.currency && <p className="aq-note">Currency was not recorded for this run; monetary values are shown without a currency symbol.</p>}
          {result.dataQuality && <details><summary>Data quality and provenance</summary><pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(result.dataQuality, null, 2)}</pre></details>}

          <div className="aq-metric-row">
            <span>
              {result.credit && (
                <>
                  Charged <strong className="num">${result.credit.charged?.toFixed?.(4)}</strong> · balance{' '}
                  <strong className="num">${result.credit.balanceAfter?.toFixed?.(2)}</strong>
                </>
              )}
            </span>
            <Chip tone="info">
              {result.meta?.engine} · {result.meta?.mode} · {result.meta?.device === 'cuda' ? 'GPU' : 'CPU'}
              {result.meta?.demoData ? ' · demo data' : ''}
            </Chip>
          </div>
        </>
      )}
    </Card>
  );
}
